import { Container, Grid, Box, Typography } from "@mui/material";
import { algoliasearch } from "algoliasearch";
import React, { useMemo, useState } from "react";
import {
  Configure,
  InstantSearch,
  useHits,
  UseHitsProps,
} from "react-instantsearch";
import ProductGalleryHit from "@/app/pages/productGallery/productGalleryHit";

interface ManufacturerProductRecommenderProps {
  warehouseCode: string;
  brand: string;
  isAuthenticated: boolean;
  productCount: number;
}

const ManufacturerProductRecommender: React.FC<
  ManufacturerProductRecommenderProps
> = ({ warehouseCode, brand, isAuthenticated, productCount }) => {
  const searchClient = useMemo(() => {
    return algoliasearch(
      process.env.REACT_APP_ALGOLIA_ID ?? "",
      process.env.REACT_APP_ALGOLIA_KEY ?? ""
    );
  }, []);

  const CustomHits = (props: UseHitsProps) => {
    const { items } = useHits(props);

    const [loadIndex, setLoadIndex] = useState(0);
    // const [currentSlide, setCurrentSlide] = useState(0);

    const handleLoadComplete = (index: number) => {
      if (index === loadIndex) setLoadIndex((prev) => prev + 1);
    };

    const searchOptionalFilters =
      warehouseCode === null || warehouseCode === ""
        ? undefined
        : [
            `sales.lvl1:${warehouseCode}`,
            `sales.lvl2:${warehouseCode}`,
            `sales.lvl3:${warehouseCode}`,
            `sales.lvl4:${warehouseCode}`,
            `sales.lvl5:${warehouseCode}`,
            `sales.lvl6:${warehouseCode}`,
            `sales.lvl7:${warehouseCode}`,
            `sales.lvl8:${warehouseCode}`,
            `sales.lvl9:${warehouseCode}`,
            `sales.lvl10:${warehouseCode}`,
            `sales.lvl11:${warehouseCode}`,
            `sales.lvl12:${warehouseCode}`,
            `sales.lvl13:${warehouseCode}`,
            `sales.lvl14:${warehouseCode}`,
            `sales.lvl15:${warehouseCode}`,
          ];

    let searchFilter = `visibleindividually:true`;
    if (warehouseCode) searchFilter += ` AND warehouses:'${warehouseCode}'`;
    if (brand) searchFilter += ` AND manufacturername:'${brand}'`;

    return (
      <Container maxWidth="lg" sx={{ p: 0 }}>
        <div
          className="product-recommender ais-Hits"
          style={{ background: "white", position: "relative" }}
        >
          <Configure
            filters={searchFilter}
            optionalFilters={searchOptionalFilters}
            hitsPerPage={productCount}
          />
          <div
            style={{
              width: "100%",
              // height: "660px",
              display: "flex",
              flexWrap: "nowrap",
              overflowX: "auto",
              overflowY: "hidden",
              gap: "16px",
              listStyle: "none",
              padding: "15px 0",
            }}
          >
            {items.map((hit: any, index: number) => {
              return (
                <Box
                  key={hit.sku}
                  component="li"
                  sx={{
                    width: "100%",
                    flex: "0 0 100%",
                    "@media (min-width: 400px)": {
                      width: "50%",
                      flex: "0 0 50%",
                    },
                    "@media (min-width: 900px)": {
                      flex: "0 0 calc(33.33% - 16px)",
                      minWidth: "calc(33.33% - 16px)",
                    },
                    height: "100%",
                  }}
                >
                  <ProductGalleryHit
                    warehouseCode={warehouseCode}
                    hit={hit}
                    position={index}
                    isAuthenticated={isAuthenticated}
                    shouldLoad={index === loadIndex}
                    onLoadComplete={() => handleLoadComplete(index)}
                    hideSkeleton={true}
                    isProductRecommenderTemplate={true}
                  />
                </Box>
              );
            })}
          </div>
        </div>
      </Container>
    );
  };

  return (
    <Grid container spacing={0} sx={{ mt: 0, p: 0, mb: 10 }}>
      <InstantSearch
        searchClient={searchClient}
        indexName={process.env.REACT_APP_ALGOLIA_PRODUCT_INDEX}
        future={{ preserveSharedStateOnUnmount: true }}
      >
        <Grid item xs={12}>
          <CustomHits />
        </Grid>
      </InstantSearch>
    </Grid>
  );
};

export default ManufacturerProductRecommender;

export const manufacturerProductRecommenderBoxRegistration = {
  name: "ManufacturerProductRecommender",
  inputs: [
    {
      name: "warehouseCode",
      type: "text",
    },
    {
      name: "brand",
      type: "text",
    },
    {
      name: "isAuthenticated",
      type: "boolean",
      defaultValue: false,
    },
    {
      name: "productCount",
      type: "number",
      defaultValue: 8,
    },
  ],
  image: "https://unpkg.com/css.gg@2.0.0/icons/svg/format-text.svg",
  description: "Add a Manufacturer Product Recommender component to the page",
  canHaveChildren: true,
  defaultChildren: [],
};
